import React from 'react';

interface HeroArchitectureProps {
  mouseX?: number; // Normalized -1 to 1
  mouseY?: number; // Normalized -1 to 1
  scrollProgress?: number; // 0 to 1
}

export const HeroArchitecture: React.FC<HeroArchitectureProps> = ({
  mouseX = 0,
  mouseY = 0,
  scrollProgress = 0,
}) => {
  // Parallax offsets for logo and atmospheric depth
  const logoOffset = {
    x: mouseX * 10,
    y: mouseY * 8,
  };
  const bgGlowOffset = {
    x: mouseX * -6,
    y: mouseY * -4,
  };
  const fgRingOffset = {
    x: mouseX * 14,
    y: mouseY * 12,
  };

  const cameraZoom = 1 + scrollProgress * 0.15;
  const cameraRotateY = mouseX * 5;
  const cameraRotateX = -mouseY * 5;

  return (
    <div 
      className="relative w-full h-[480px] sm:h-[560px] md:h-[620px] lg:h-[680px] flex items-center justify-center perspective-stage select-none"
      style={{ perspective: '1600px' }}
    >
      {/* 3D Root Container */}
      <div 
        className="relative w-full h-full flex items-center justify-center transition-transform duration-300 ease-out preserve-3d"
        style={{
          transform: `scale(${cameraZoom}) rotateY(${cameraRotateY}deg) rotateX(${cameraRotateX}deg)`,
        }}
      >
        {/* =========================================================================
            DEPTH LAYER 1: Ambient Golden Halo & Concentric Diplomatic Orbits
           ========================================================================= */}
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none preserve-3d"
          style={{
            transform: `translate3d(${bgGlowOffset.x}px, ${bgGlowOffset.y}px, -120px)`,
            transition: 'transform 0.15s ease-out'
          }}
        >
          {/* Warm Bronze/Beige Halo */}
          <div className="w-[380px] sm:w-[480px] md:w-[540px] h-[380px] sm:h-[480px] md:h-[540px] bg-gradient-to-b from-beige-300/15 via-bronze/10 to-transparent rounded-full blur-3xl opacity-75" />

          {/* Classical Geometric Compass Ring */}
          <div className="absolute w-[360px] sm:w-[460px] md:w-[520px] h-[360px] sm:h-[460px] md:h-[520px] rounded-full border border-bronze/25 opacity-40 animate-[spin_120s_linear_infinite]" />
          <div className="absolute w-[420px] sm:w-[540px] md:w-[600px] h-[420px] sm:h-[540px] md:h-[600px] rounded-full border border-dashed border-beige/15 opacity-30" />
        </div>

        {/* =========================================================================
            DEPTH LAYER 2: The Official Future Leaders Summit Logo
           ========================================================================= */}
        <div 
          className="relative z-10 w-full max-w-[460px] sm:max-w-[540px] md:max-w-[580px] lg:max-w-[620px] px-2 sm:px-4 flex flex-col items-center justify-center preserve-3d group"
          style={{
            transform: `translate3d(${logoOffset.x}px, ${logoOffset.y}px, 20px)`,
            transition: 'transform 0.12s ease-out'
          }}
        >
          {/* Official Summit Emblem Image */}
          <div className="relative w-full flex items-center justify-center">
            {/* Ambient drop shadow matching classical stone sculpture */}
            <div className="absolute inset-0 bg-black/40 filter blur-xl rounded-full scale-90" />

            <img
              src="/logo.png"
              alt="FLSxMUN 2.0 Official Logo"
              className="relative z-10 w-full max-h-[460px] sm:max-h-[540px] md:max-h-[600px] lg:max-h-[640px] object-contain drop-shadow-[0_25px_45px_rgba(0,0,0,0.85)] filter contrast-[1.04] brightness-[1.01] transition-transform duration-500 hover:scale-[1.02]"
              style={{
                imageRendering: '-webkit-optimize-contrast',
              }}
              loading="eager"
            />
          </div>

          {/* Classical Base Horizon Accent */}
          <div className="w-64 sm:w-80 h-[1px] bg-gradient-to-r from-transparent via-beige/40 to-transparent mt-3 opacity-60" />
        </div>

        {/* =========================================================================
            DEPTH LAYER 3: Foreground Editorial Coordinates & Framing Brackets
           ========================================================================= */}
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none preserve-3d"
          style={{
            transform: `translate3d(${fgRingOffset.x}px, ${fgRingOffset.y}px, 60px)`,
            transition: 'transform 0.1s ease-out'
          }}
        >
          <div className="w-full max-w-[480px] sm:max-w-[560px] md:max-w-[600px] lg:max-w-[640px] h-[480px] sm:h-[560px] md:h-[600px] lg:h-[640px] border border-bronze/20 relative flex flex-col justify-between p-3 sm:p-4">
            {/* Top Corners Accent */}
            <div className="flex justify-between items-start">
              <div className="w-5 h-5 border-t-2 border-l-2 border-beige-300/80" />
              <div className="w-5 h-5 border-t-2 border-r-2 border-beige-300/80" />
            </div>

            {/* Bottom Corners Accent */}
            <div className="flex justify-between items-end">
              <div className="w-5 h-5 border-b-2 border-l-2 border-beige-300/80" />
              <div className="w-5 h-5 border-b-2 border-r-2 border-beige-300/80" />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
